import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Cpu } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Checkbox,
  SegmentedControl,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  serviceApi,
  onPath,
  onKeys,
  unwrap,
  useInstanceConfig,
  useOpennebula,
  parseFlavorName,
  type Instance,
  type ConfigFlavor,
} from '@/api/opennebula';

type FlavorType = 'standard' | 'processor-optimized' | 'memory-optimized' | 'gpu';
const TYPE_LABELS: Record<string, string> = {
  standard: 'Standard',
  'processor-optimized': 'Processor',
  'memory-optimized': 'Memory',
  gpu: 'GPU',
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Resize an instance to a different flavor. Mirrors the legacy flow: power the VM
 * off first (if running), apply the new flavor via PATCH, then resume if it was
 * running. Rendered as a modal at `/instances/:id/resize`.
 */
export function InstanceResize({ serviceId, instance }: { serviceId: string; instance: Instance }) {
  const navigate = useNavigate();
  const { instanceId } = useParams<{ instanceId: string }>();
  const { show } = useToast();
  const { setWaiting } = useOpennebula();
  const qc = useQueryClient();
  const { data: config } = useInstanceConfig(serviceId);

  const base = `/services/compute/${serviceId}/instances/${instanceId}`;
  const close = () => navigate(base);

  const flavors = config?.flavors ?? [];
  const types = useMemo(() => {
    const present = [...new Set(flavors.map((f) => f.type).filter(Boolean))] as FlavorType[];
    return present.map((t) => ({ value: t, label: TYPE_LABELS[t] ?? t }));
  }, [flavors]);

  const current = instance.metadata?.flavor;
  const currentType = (flavors.find((f) => f.name === current)?.type as FlavorType) ?? 'standard';
  const [type, setType] = useState<FlavorType>(currentType);
  const [flavor, setFlavor] = useState<string>(current ?? '');
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  const visible = flavors.filter((f) => f.type === type);
  const isOff = instance.status === 'POWERED_OFF';
  const needsPowerOff = !isOff;

  async function waitForPowerOff(maxAttempts = 40, interval = 3000): Promise<boolean> {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const body = unwrap<{ instance?: { status?: string } }>(
          await serviceApi.get(onPath(serviceId, `instances/${instanceId}/summary`)),
        );
        if (body.instance?.status === 'POWERED_OFF') return true;
      } catch {
        /* keep polling */
      }
      await sleep(interval);
    }
    return false;
  }

  async function resize() {
    if (!flavor) return;
    setBusy(true);
    setWaiting('resize');
    const wasRunning = instance.status === 'POWERED_ON';
    const { cores, memory } = parseFlavorName(flavor);
    try {
      if (needsPowerOff) {
        setStatusMsg('Powering off to apply the new configuration…');
        await serviceApi.post(onPath(serviceId, `instances/${instanceId}/perform`), { perform: 'poweroff' });
        const off = await waitForPowerOff();
        if (!off) throw new Error('Timed out waiting for the instance to power off.');
      }
      setStatusMsg('Applying the new instance type…');
      await serviceApi.patch(onPath(serviceId, `instances/${instanceId}`), { flavor, cores, memory });
      if (wasRunning) {
        setStatusMsg('Restarting the instance…');
        await serviceApi.post(onPath(serviceId, `instances/${instanceId}/perform`), { perform: 'resume' });
      }
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, instanceId ?? '') });
      qc.invalidateQueries({ queryKey: onKeys.instances(serviceId) });
      show({ tone: 'success', title: 'Instance resized' });
      close();
    } catch (e) {
      show({ tone: 'danger', title: (e as Error)?.message || 'Resize failed' });
    } finally {
      setBusy(false);
      setStatusMsg('');
      // This flow awaits every step it dispatches, so by here the resize is
      // over either way. Lowering the flag hands the instance back to the
      // detail page's own polling, which follows it until it is running again.
      setWaiting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) close(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Resize instance</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ marginTop: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            Current type: <b>{current || '—'}</b>. Pick a new instance type below.
          </p>

          {types.length > 1 && (
            <SegmentedControl
              items={types}
              value={type}
              onValueChange={(v) => { setType(v as FlavorType); }}
              aria-label="Flavor type"
            />
          )}

          {visible.length === 0 && (
            <Alert tone="info" style={{ marginTop: 'var(--vn-space-3)' }}>
              No instance types are available in this category.
            </Alert>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 'var(--vn-space-2)', marginTop: 'var(--vn-space-3)' }}>
            {visible.map((f: ConfigFlavor) => {
              const sel = f.name === flavor;
              return (
                <button
                  key={f.name}
                  type="button"
                  disabled={f.disabled || busy}
                  onClick={() => setFlavor(f.name)}
                  style={{
                    display: 'grid', gap: 2, textAlign: 'left', cursor: f.disabled ? 'not-allowed' : 'pointer', font: 'inherit',
                    color: f.disabled ? 'var(--vn-fg-subtle)' : 'var(--vn-fg-default)', opacity: f.disabled ? 0.5 : 1,
                    border: `1px solid ${sel ? 'var(--vn-accent-solid)' : 'var(--vn-border-default)'}`,
                    outline: sel ? '1px solid var(--vn-accent-solid)' : 'none',
                    borderRadius: 'var(--vn-radius-md)', background: 'var(--vn-bg-surface)', padding: 'var(--vn-space-3)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{f.cores} vCPU</span>
                  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{f.memory} GB RAM</span>
                  <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>{f.name}{f.name === current ? ' · current' : ''}</span>
                </button>
              );
            })}
          </div>

          <Alert tone="warning" style={{ marginTop: 'var(--vn-space-4)' }}>
            <b>Important:</b> the instance {needsPowerOff ? 'will be powered off and restarted' : 'will be updated'} to
            apply the new configuration. Unsaved work may be lost; this typically takes 3–5 minutes.
          </Alert>

          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginTop: 'var(--vn-space-3)', fontSize: 'var(--vn-font-size-sm)' }}>
            <Checkbox checked={understood} onCheckedChange={(c) => setUnderstood(c === true)} disabled={busy} />
            I understand this operation {needsPowerOff ? 'will restart my instance' : 'will'} apply the new configuration.
          </label>

          {busy && statusMsg && (
            <p style={{ marginBottom: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{statusMsg}</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={close} disabled={busy}>Cancel</Button>
          <Button type="button" startIcon={<Cpu size={15} />} onClick={resize} disabled={busy || !understood || !flavor || flavor === current}>
            {busy ? 'Resizing…' : 'Proceed with resize'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
